import { useState } from 'react';
import { Briefcase, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateEvent } from '@/hooks/useEvents';
import type { CreateEventBody } from '@/api/events.api';

/**
 * Form de configuración del minijuego "Maletín Millonario" (familia social).
 *
 * Clona la pantalla de fedesuete (ver fede-maletin.png):
 *  - Nombre
 *  - Depósito mínimo ($)
 *  - Cantidad de números
 *  - Premio por ganador ($)
 *  - Tipo de premio (Bono 200% recomendado)
 *  - Duración (minutos)
 *  + preview de los maletines + botones "Guardar Borrador" / "Crear y Activar".
 *
 * Genera el `config` JSONB y llama `eventsApi.create({ type:'maletin', ... })`
 * vía el hook `useCreateEvent` (invalida queries + toast).
 */

type PrizeKind = 'bono200' | 'bono100' | 'fichas';

const PRIZE_OPTIONS: { value: PrizeKind; label: string }[] = [
  { value: 'bono200', label: 'Bono 200% (recomendado)' },
  { value: 'bono100', label: 'Bono 100%' },
  { value: 'fichas', label: 'Fichas' },
];

interface MaletinFormProps {
  /** Callback opcional tras crear (cerrar dialog, refrescar tab, etc.). */
  onCreated?: () => void;
}

export function MaletinForm({ onCreated }: MaletinFormProps) {
  const createMutation = useCreateEvent();

  const [name, setName] = useState('');
  const [depositMin, setDepositMin] = useState('2000');
  const [numbers, setNumbers] = useState('5');
  const [amount, setAmount] = useState('2000');
  const [prizeKind, setPrizeKind] = useState<PrizeKind>('bono200');
  const [durationMin, setDurationMin] = useState('60');
  const [error, setError] = useState('');

  const numCount = Math.max(2, Math.min(9, Number(numbers) || 5));
  const previewNumbers = Array.from({ length: numCount }, (_, i) => i + 1);

  const buildBody = (status: 'draft' | 'active'): CreateEventBody => {
    const n = Math.max(2, Math.min(9, Number(numbers) || 5));
    const amt = Number(amount) || 0;
    const dep = Number(depositMin) || 0;
    const dur = Number(durationMin) || 0;

    return {
      // 'maletin' no está en el union EventType compartido (events.api.ts);
      // casteamos sin tocar el archivo compartido (lo amplía el orquestador).
      type: 'maletin' as unknown as CreateEventBody['type'],
      name: name.trim(),
      depositMin: dep > 0 ? String(dep) : undefined,
      prizeKind,
      prizeAmount: amt > 0 ? String(amt) : undefined,
      prizeDescription:
        prizeKind === 'bono200'
          ? 'Solo Bono 200% en próxima carga (sin fichas)'
          : undefined,
      durationMin: dur,
      status,
      config: {
        numbers: n,
        prizeKind,
        amount: amt,
        depositMin: dep,
        wagerMultiplier: 1,
      },
    };
  };

  const submit = async (status: 'draft' | 'active') => {
    setError('');
    if (!name.trim()) {
      setError('El nombre es requerido');
      return;
    }
    try {
      await createMutation.mutateAsync(buildBody(status));
      // reset
      setName('');
      setDepositMin('2000');
      setNumbers('5');
      setAmount('2000');
      setPrizeKind('bono200');
      setDurationMin('60');
      onCreated?.();
    } catch {
      /* el hook ya muestra toast de error */
    }
  };

  const pending = createMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Briefcase className="h-5 w-5 text-amber-500" />
        <h3 className="text-lg font-semibold text-amber-500">
          Crear Maletín Millonario
        </h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Cuando un jugador deposita ≥ ${Number(depositMin || 0).toLocaleString('es-AR')},
        le aparecen {numCount} números para elegir. Al terminar, el número con
        MENOS votos gana. Si hay empate, se elige al azar.
      </p>

      <div className="space-y-2">
        <Label htmlFor="maletin-name">
          Nombre <span className="text-red-500">*</span>
        </Label>
        <Input
          id="maletin-name"
          placeholder="ej: Maletín Nocturno"
          value={name}
          onChange={(e) => setName((e.target as HTMLInputElement).value)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="maletin-deposit">Depósito mínimo ($)</Label>
          <Input
            id="maletin-deposit"
            type="number"
            min="0"
            value={depositMin}
            onChange={(e) =>
              setDepositMin((e.target as HTMLInputElement).value)
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="maletin-numbers">Cantidad de números</Label>
          <Input
            id="maletin-numbers"
            type="number"
            min="2"
            max="9"
            value={numbers}
            onChange={(e) => setNumbers((e.target as HTMLInputElement).value)}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="maletin-amount">Premio por ganador ($)</Label>
          <Input
            id="maletin-amount"
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount((e.target as HTMLInputElement).value)}
          />
          <p className="text-xs text-muted-foreground">
            Solo Bono 200% en próxima carga (sin fichas)
          </p>
        </div>
        <div className="space-y-2">
          <Label>Tipo de premio</Label>
          <Select
            value={prizeKind}
            onValueChange={(v) => setPrizeKind(v as PrizeKind)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIZE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2 md:max-w-[12rem]">
        <Label htmlFor="maletin-duration">Duración (minutos)</Label>
        <Input
          id="maletin-duration"
          type="number"
          min="0"
          value={durationMin}
          onChange={(e) => setDurationMin((e.target as HTMLInputElement).value)}
        />
      </div>

      {/* Preview de los maletines (clon del screenshot) */}
      <div className="flex flex-wrap gap-2 pt-1">
        {previewNumbers.map((n) => (
          <div
            key={n}
            className="flex h-12 w-12 items-center justify-center rounded-md bg-amber-400 text-base font-bold text-amber-950 shadow-sm"
          >
            {n}
          </div>
        ))}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex flex-wrap gap-3 pt-2">
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => submit('draft')}
        >
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Guardar Borrador
        </Button>
        <Button
          className="bg-amber-500 text-amber-950 hover:bg-amber-400"
          disabled={pending}
          onClick={() => submit('active')}
        >
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear y Activar
        </Button>
      </div>
    </div>
  );
}

export default MaletinForm;
